import {lazy, Suspense, useEffect, type ComponentType, type LazyExoticComponent} from 'react';
import {Navigate, Route, Routes as BrowserRoutes, useLocation, useNavigate} from 'react-router-dom';
import {observer} from 'mobx-react';
import AppStore from './app.store';
import LandingLoginRedirect from './components/landing-redirect';
import RoutePageFallback from './components/route-page-fallback';
import {PermissionGroup} from './utils/permission';
import {hasPermission} from './utils/hasPermission';
import {ENABLE_BILLING} from './config/features';

const lazyDefault = (importFn: () => Promise<{ default: ComponentType }>) => lazy(importFn);

const lazyNamed = <K extends string>(
    importFn: () => Promise<Record<K, ComponentType>>,
    exportName: K,
) => lazy(() => importFn().then((module) => ({default: module[exportName]})));

const AuthView = lazyDefault(() => import('./pages/auth/auth.view'));
const SummaryView = lazyDefault(() => import('./pages/reports/summary/summary.view'));
const NotificationsView = lazyDefault(() => import('./pages/notifications/notifications.view'));
const TransactionsView = lazyDefault(() => import('./pages/transactions/transactions.view'));
const TransactionsServicesView = lazyDefault(() => import('./pages/transactions.services/transactions.services.view'));
const OrdersView = lazyDefault(() => import('./pages/orders/orders.view'));
const ShipmentsView = lazyDefault(() => import('./pages/shipments/shipments.view'));
const BusinessUnitsView = lazyDefault(() => import('./pages/business_units/business_units.view'));
const Stock = lazyNamed(() => import('./pages/stock/stock.view'), 'Stock');
const Transfers = lazyNamed(() => import('./pages/transfer_of_goods/transfer.view'), 'Transfers');
const ProductsView = lazyDefault(() => import('./pages/products/products.view'));
const BrandsView = lazyDefault(() => import('./pages/brands/brands.view'));
const ProductCategoriesView = lazyDefault(() => import('./pages/product_categories/product_categories.view'));
const GoodsMovementView = lazyDefault(() => import('./pages/goods_movement/goods_movement.view'));
const PurchaseBillsView = lazyDefault(() => import('./pages/purchase.bills/purchase.bills.view'));
const PurchaseBillsOperationsView = lazyDefault(() => import('./pages/purchase.bills.operations/purchase.bills.operations.view'));
const BusinessServiceView = lazyDefault(() => import('./pages/business_services/business_service.view'));
const RolesView = lazyDefault(() => import('./pages/roles/roles.view'));
const UsersView = lazyDefault(() => import('./pages/users/users.view'));
const ClientsView = lazyDefault(() => import('./pages/clients/clients.view'));
const ClientsDebtsView = lazyDefault(() => import('./pages/clients/clients.debts/clients.debts.view'));
const ClientsDebtsOperationsView = lazyDefault(() => import('./pages/clients/clients.debts.operations/clients.debts.operations.view'));
const SalesReportView = lazyDefault(() => import('./pages/reports/sales.report/sales.report.view'));
const FinReportsView = lazyDefault(() => import('./pages/reports/fin.reports/fin.reports.view'));
const MainView = lazyDefault(() => import('./pages/main/main.view'));
const SuppliersView = lazyDefault(() => import('./pages/suppliers/suppliers.view'));
const StatusesView = lazyDefault(() => import('./pages/statuses/statuses.view'));
const AllowancesView = lazyDefault(() => import('./pages/payrolls/allowances/allowances.view'));
const DeductionsView = lazyDefault(() => import('./pages/payrolls/deductions/deductions.view'));
const EmployeeAllowancesView = lazyDefault(() => import('./pages/payrolls/employee.allowances/employee.allowances.view'));
const EmployeeDeductionsView = lazyDefault(() => import('./pages/payrolls/employee.deductions/employee.deductions.view'));
const PayrollsView = lazyDefault(() => import('./pages/payrolls/payrolls/payrolls.view'));
const ExpensesView = lazyDefault(() => import('./pages/expenses/expenses/expenses.view'));
const ExpenseCategoriesView = lazyDefault(() => import('./pages/expenses/expense.categories/expense.categories.view'));
const ExpenseOperationsView = lazyDefault(() => import('./pages/expenses/expense.operations/expense.operations.view'));
const OwnerSettingsView = lazyDefault(() => import('./pages/owner.settings/owner.settings/owner.settings.view'));
const OnboardingView = lazyDefault(() => import('./pages/onboarding/onboarding.view'));
const ExchangeRatesView = lazyDefault(() => import('./pages/exchange.rates/exchange.rates.view'));
const InstallmentsView = lazyDefault(() => import('./pages/installments/installments/installments.view'));
const InstallmentLinesView = lazyDefault(() => import('./pages/installments/installment.lines/installment.lines.view'));
const InstallmentConditionsView = lazyDefault(() => import('./pages/installments/installment.conditions/installment.conditions.view'));
const InstallmentCardsView = lazyDefault(() => import('./pages/installments/installmet.cards/installment.cards.view'));
const InstallmentCashbackView = lazyDefault(() => import('./pages/installments/installment.cashback/installment.cashback.view'));
const DelegatedSettlementsView = lazyDefault(() => import('./pages/delegated_settlements/delegated.settlements.view'));
const InventoryAdjustmentsView = lazyDefault(() => import('./pages/inventory.adjustments/inventory.adjustments.view'));
const SmsModuleMessagesView = lazyDefault(() => import('./pages/sms.module/sms.module.messages/sms.module.messages.view'));
const SmsModuleTemplatesView = lazyDefault(() => import('./pages/sms.module/sms.module.templates/sms.module.templates.view'));
const SmsModuleGroupsView = lazyDefault(() => import('./pages/sms.module/sms.module.groups/sms.module.groups.view'));
const SmsModulePlansView = lazyDefault(() => import('./pages/sms.module/sms.module.plans/sms.module.plans.view'));
const CrmIssuesView = lazyDefault(() => import('./pages/crm/crm.issues/crm.issues.view'));
const CrmInteractionsView = lazyDefault(() => import('./pages/crm/crm.interactions/crm.interactions.view'));
const CrmCampaignsView = lazyDefault(() => import('./pages/crm/crm.campaigns/crm.campaigns.view'));
const CrmStatisticsView = lazyDefault(() => import('./pages/crm/crm.statistics/crm.statistics.view'));
const OwnerIntegrationsView = lazyDefault(() => import('./pages/owner.settings/owner.intergrations/owner.integrations.view'));
const ClientsStatusesView = lazyDefault(() => import('./pages/clients/clients.statuses/clients.statuses.view'));
const ClientsLeadQualitiesView = lazyDefault(() => import('./pages/clients/clients.lead.qualities/clients.lead.qualities.view'));
const ProductCreateRedirect = () => <Navigate to="/products/list?create=1" replace />;
const ProductUpdateRedirect = () => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    return <Navigate to={id ? `/products/list?edit=${id}` : '/products/list'} replace />;
};
const BannersView = lazyDefault(() => import('./pages/online.shop/banners/banners.view'));
const OnlineShopProductsPage = lazyDefault(() => import('./pages/online.shop/products/online.shop.products.page'));
const OnlineShopSettingsView = lazyDefault(() => import('./pages/online.shop/settings/online.shop.settings.view'));
const OnlineShopBrandsPage = lazyDefault(() => import('./pages/online.shop/brands/online.shop.brands.page'));
const OnlineShopCategoriesPage = lazyDefault(() => import('./pages/online.shop/categories/online.shop.categories.page'));
const OnlineShopMainPagePage = lazyDefault(() => import('./pages/online.shop/settings/main.page/online.shop.main.page.page'));
const CashSessionsView = lazyDefault(() => import('./pages/cash_sessions/cash_sessions.view'));
const ClientsBlacklistsView = lazyDefault(() => import('./pages/clients/clients.blacklists/clients.blacklists.view'));
const SubscriptionView = lazyDefault(() => import('./pages/billing/subscription/subscription.view'));

type LazyRoute = {
    path: string;
    Component: LazyExoticComponent<ComponentType>;
    hidden?: boolean;
};

type StaticRoute = {
    path: string;
    element: JSX.Element;
    hidden?: boolean;
};

type AppRoute = LazyRoute | StaticRoute;

const isLazyRoute = (route: AppRoute): route is LazyRoute => 'Component' in route;

const Routes = observer(() => {
    const {permissions, needsOnboarding, userData} = AppStore;
    const location = useLocation();
    const navigate = useNavigate();

    const shouldForceOnboarding =
        Boolean(permissions) &&
        needsOnboarding &&
        Number(userData?.role_id) === 1 &&
        location.pathname !== '/onboarding' &&
        location.pathname !== '/subscription' &&
        location.pathname !== '/auth' &&
        location.pathname !== '/login';

    // Редирект через navigate (useEffect), а не <Navigate /> вне <Routes> —
    // иначе контент остаётся null и онбординг не открывается.
    useEffect(() => {
        if (shouldForceOnboarding) {
            navigate('/onboarding', {replace: true});
        }
    }, [shouldForceOnboarding, navigate]);

    if (!permissions) {
        return (
            <Suspense fallback={<RoutePageFallback/>}>
                <BrowserRoutes>
                    <Route path="/login" element={<LandingLoginRedirect/>}/>
                    <Route path="/auth" element={<AuthView/>}/>
                    {ENABLE_BILLING
                        ? <Route path="/subscription" element={<SubscriptionView/>}/>
                        : <Route path="/subscription" element={<Navigate to="/" replace/>}/>}
            </BrowserRoutes>
            </Suspense>
        );
    }

    // Пока уходим на онбординг — не показываем главную
    if (shouldForceOnboarding) {
        return <RoutePageFallback />;
    }

    const routes: AppRoute[] = [
        {
            path: '/summary',
            Component: SummaryView,
            hidden: !hasPermission(permissions, PermissionGroup.REPORTS, 'READ'),
        },
        {
            path: '/notifications',
            Component: NotificationsView,
        },
        {
            path: '/subscription',
            Component: SubscriptionView,
            hidden: !ENABLE_BILLING,
        },
        {
            path: '/transactions/list',
            Component: TransactionsView,
            hidden: !hasPermission(permissions, PermissionGroup.TRANSACTIONS, 'READ'),
        },
        {
            path: '/transactions/services',
            Component: TransactionsServicesView,
            hidden: !hasPermission(permissions, PermissionGroup.TRANSACTIONS, 'UPDATE'),
        },
        {
            path: '/transactions/orders',
            Component: OrdersView,
            hidden: !hasPermission(permissions, PermissionGroup.ORDER_REQUESTS, 'READ'),
        },
        {
            path: '/business_stock/shipments',
            Component: ShipmentsView,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/business_units',
            Component: BusinessUnitsView,
            hidden: !hasPermission(permissions, PermissionGroup.BUSINESS_UNITS, 'READ'),
        },
        {
            path: '/business_stock/list',
            Component: Stock,
            hidden: !hasPermission(permissions, PermissionGroup.BUSINESS_STOCKS, 'READ'),
        },
        {
            path: '/business_stock/transfers',
            Component: Transfers,
            hidden: !hasPermission(permissions, PermissionGroup.TRANSFERS, 'READ'),
        },
        {
            path: '/products/list',
            Component: ProductsView,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/products/brands',
            Component: BrandsView,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCT_BRANDS, 'READ'),
        },
        {
            path: '/products/categories',
            Component: ProductCategoriesView,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCT_CATEGORIES, 'READ'),
        },
        {
            path: '/business_stock/movements',
            Component: GoodsMovementView,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/business_stock/bills',
            Component: PurchaseBillsView,
            hidden: !hasPermission(permissions, PermissionGroup.BUSINESS_STOCKS, 'READ'),
        },
        {
            path: '/business_stock/bills_operations',
            Component: PurchaseBillsOperationsView,
            hidden: !hasPermission(permissions, PermissionGroup.BUSINESS_STOCKS, 'READ'),
        },
        {
            path: '/cashier/services',
            Component: BusinessServiceView,
            hidden: !hasPermission(permissions, PermissionGroup.BUSINESS_SERVICES, 'UPDATE'),
        },
        {
            path: '/roles',
            Component: RolesView,
            hidden: !hasPermission(permissions, PermissionGroup.ROLES, 'READ'),
        },
        {
            path: '/users',
            Component: UsersView,
            hidden: !hasPermission(permissions, PermissionGroup.USERS, 'READ'),
        },
        {
            path: '/clients/list',
            Component: ClientsView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENTS, 'READ'),
        },
        {
            path: '/clients/debts',
            Component: ClientsDebtsView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENTS, 'READ'),
        },
        {
            path: '/clients/payments',
            Component: ClientsDebtsOperationsView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENTS, 'READ'),
        },
        {
            path: '/reports/sales',
            Component: SalesReportView,
            hidden: !hasPermission(permissions, PermissionGroup.REPORTS, 'READ'),
        },
        {
            path: '/reports/financial',
            Component: FinReportsView,
            hidden: !hasPermission(permissions, PermissionGroup.REPORTS, 'READ'),
        },
        {
            path: '/',
            Component: MainView,
        },
        {
            path: '/products/suppliers',
            Component: SuppliersView,
            hidden: !hasPermission(permissions, PermissionGroup.SUPPLIERS, 'READ'),
        },
        {
            path: '/settings/statuses',
            Component: StatusesView,
            hidden: !hasPermission(permissions, PermissionGroup.STATUSES, 'READ'),
        },
        {
            path: '/payrolls/allowances',
            Component: AllowancesView,
            hidden: !hasPermission(permissions, PermissionGroup.PAYROLLS, 'READ'),
        },
        {
            path: '/payrolls/deductions',
            Component: DeductionsView,
            hidden: !hasPermission(permissions, PermissionGroup.PAYROLLS, 'READ'),
        },
        {
            path: '/payrolls/employee/allowances',
            Component: EmployeeAllowancesView,
            hidden: !hasPermission(permissions, PermissionGroup.PAYROLLS, 'READ'),
        },
        {
            path: '/payrolls/employee/deductions',
            Component: EmployeeDeductionsView,
            hidden: !hasPermission(permissions, PermissionGroup.PAYROLLS, 'READ'),
        },
        {
            path: '/payrolls/payments',
            Component: PayrollsView,
            hidden: !hasPermission(permissions, PermissionGroup.PAYROLLS, 'READ'),
        },
        {
            path: '/expenses/list',
            Component: ExpensesView,
            hidden: !hasPermission(permissions, PermissionGroup.EXPENSES, 'READ'),
        },
        {
            path: '/expenses/categories',
            Component: ExpenseCategoriesView,
            hidden: !hasPermission(permissions, PermissionGroup.EXPENSES, 'READ'),
        },
        {
            path: '/expenses/operations',
            Component: ExpenseOperationsView,
            hidden: !hasPermission(permissions, PermissionGroup.EXPENSES, 'READ'),
        },
        {
            path: '/settings/business',
            Component: OwnerSettingsView,
            hidden: AppStore.userData?.role_id !== 1,
        },
        {
            path: '/onboarding',
            Component: OnboardingView,
            hidden: Number(AppStore.userData?.role_id) !== 1,
        },
        {
            path: '/settings/exchange_rates',
            Component: ExchangeRatesView,
            hidden: !hasPermission(permissions, PermissionGroup.EXCHANGE_RATES, 'READ'),
        },
        {
            path: '/installments/list',
            Component: InstallmentsView,
            hidden: !hasPermission(permissions, PermissionGroup.INSTALLMENTS, 'READ'),
        },
        {
            path: '/installments/lines',
            Component: InstallmentLinesView,
            hidden: !hasPermission(permissions, PermissionGroup.INSTALLMENT_LINES, 'READ'),
        },
        {
            path: '/installments/conditions',
            Component: InstallmentConditionsView,
            hidden: !hasPermission(permissions, PermissionGroup.INSTALLMENTS, 'READ'),
        },
        {
            path: '/installments/cards',
            Component: InstallmentCardsView,
            hidden: !hasPermission(permissions, PermissionGroup.INSTALLMENT_CARDS, 'READ'),
        },
        {
            path: '/installments/cashback',
            Component: InstallmentCashbackView,
            hidden: AppStore.userData?.role_id !== 1
                && !hasPermission(permissions, PermissionGroup.INSTALLMENT_CASHBACK, 'READ'),
        },
        {
            path: '/delegated-services/settlements',
            Component: DelegatedSettlementsView,
            hidden: !AppStore.businessSettings?.delegated_services
                || (AppStore.userData?.role_id !== 1
                    && !hasPermission(permissions, PermissionGroup.TRANSACTIONS, 'READ')),
        },
        {
            path: '/installments/settlements',
            element: <Navigate to="/delegated-services/settlements" replace/>,
        },
        {
            path: '/business_stock/adjustments',
            Component: InventoryAdjustmentsView,
            hidden: !hasPermission(permissions, PermissionGroup.INVENTORY_ADJUSTMENTS, 'READ'),
        },
        {
            path: '/sms/list',
            Component: SmsModuleMessagesView,
            hidden: !hasPermission(permissions, PermissionGroup.SMS_MESSAGES, 'READ'),
        },
        {
            path: '/sms/templates',
            Component: SmsModuleTemplatesView,
            hidden: !hasPermission(permissions, PermissionGroup.SMS_TEMPLATES, 'READ'),
        },
        {
            path: '/sms/groups',
            Component: SmsModuleGroupsView,
            hidden: !hasPermission(permissions, PermissionGroup.SMS_CONTACT_GROUPS, 'READ'),
        },
        {
            path: '/sms/plans',
            Component: SmsModulePlansView,
            hidden: !hasPermission(permissions, PermissionGroup.SMS_PLAN, 'READ'),
        },
        {
            path: '/crm/issues',
            Component: CrmIssuesView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENT_ISSUES, 'READ'),
        },
        {
            path: '/crm/campaigns',
            Component: CrmCampaignsView,
            hidden: !hasPermission(permissions, PermissionGroup.CAMPAIGNS, 'READ'),
        },
        {
            path: '/clients/statuses',
            Component: ClientsStatusesView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENT_STATUSES, 'READ'),
        },
        {
            path: '/clients/lead_quality',
            Component: ClientsLeadQualitiesView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENT_LEAD_QUALITY, 'READ'),
        },
        {
            path: '/crm/interactions',
            Component: CrmInteractionsView,
        },
        {
            path: '/crm/statistics',
            Component: CrmStatisticsView,
        },
        {
            path: '/settings/integrations',
            Component: OwnerIntegrationsView,
            hidden: AppStore.userData?.role_id !== 1,
        },
        {
            path: '/products/create',
            element: <ProductCreateRedirect/>,
        },
        {
            path: '/products/update',
            element: <ProductUpdateRedirect/>,
        },
        {
            path: '/online_shop/banners',
            Component: BannersView,
            hidden: !hasPermission(permissions, PermissionGroup.BANNERS, 'READ'),
        },
        {
            path: '/online_shop/main',
            Component: OnlineShopMainPagePage,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/online_shop/categories',
            Component: OnlineShopCategoriesPage,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/online_shop/brands',
            Component: OnlineShopBrandsPage,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/online_shop/products',
            Component: OnlineShopProductsPage,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/online_shop/settings',
            Component: OnlineShopSettingsView,
            hidden: !hasPermission(permissions, PermissionGroup.PRODUCTS, 'READ'),
        },
        {
            path: '/cash-sessions',
            Component: CashSessionsView,
            hidden: !hasPermission(permissions, PermissionGroup.CASHIER_SESSION, 'READ'),
        },
        {
            path: '/clients/blacklists',
            Component: ClientsBlacklistsView,
            hidden: !hasPermission(permissions, PermissionGroup.CLIENT_BLACKLIST, 'READ'),
        },
    ];

    return (
        <Suspense fallback={<RoutePageFallback/>}>
            <BrowserRoutes>
                <Route path="/login" element={<LandingLoginRedirect/>}/>
                {routes
                    .filter((route) => !route.hidden)
                    .map((route) => (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={
                                isLazyRoute(route)
                                    ? <route.Component/>
                                    : route.element
                            }
                        />
                    ))}
                {!ENABLE_BILLING && (
                    <Route path="/subscription" element={<Navigate to="/" replace/>}/>
                )}
            </BrowserRoutes>
        </Suspense>
    );
});

export default Routes;
